import { useState } from 'react';
import { api, type BudgetProgress } from '../api.ts';
import { useApp, useFetch, useMoney, usePrivateMoney } from '../store.tsx';
import { Card, Empty, Field, ProvenanceBadge, SectionTitle, Sheet, Skeleton } from '../components.tsx';
import { formatDate, parseAmountInput } from '../format.ts';
import { minorToDecimal } from '@fintrack/core';

interface Goal {
  id: string; name: string; targetAmountMinor: number; currentAmountMinor: number;
  remainingMinor: number; progressPct: number; targetDate: string | null;
  monthsRemaining: number | null; requiredMonthlyMinor: number | null;
  estimatedMonthlySavingMinor: number | null; onTrack: boolean | null; provenance: string;
  /** Credited to this goal from the cycle currently open. */
  thisCycleMinor?: number;
}

interface Suggestion {
  category: string; suggestedAmountMinor: number; basedOnMonths: number; averageMinor: number;
  /** Why this category was picked out of all the ones the user spends in. */
  reason?: 'varies' | 'rising';
  /** The gap between the dearest month and the cheapest, which is what a limit bites on. */
  swingMinor?: number;
}

/**
 * What the budget sheet is being opened to do.
 *
 * One sheet for all three, because they are the same form — a category and a
 * monthly limit — and a second editor would be a second place for the amount
 * rules to drift. The shape is a union rather than a pile of optional flags so
 * that "editing" cannot be half-set: an edit always carries the id it will
 * write back to.
 */
type BudgetSheetMode =
  | { kind: 'create' }
  | { kind: 'suggestion'; category: string; amountMinor: number }
  | { kind: 'edit'; id: string; category: string; amountMinor: number };

/**
 * How a budget's window is described.
 *
 * A budget is a limit over a window, and the window was nowhere on screen. With
 * the home card measuring it against the selected period — Today, by default —
 * a monthly limit visibly reset every midnight, which is indistinguishable from
 * a daily limit.
 */
const PERIOD_WORD: Record<string, { en: string; ar: string }> = {
  weekly: { en: 'this week', ar: 'هذا الأسبوع' },
  monthly: { en: 'this month', ar: 'هذا الشهر' },
  yearly: { en: 'this year', ar: 'هذا العام' },
};

/** Budgets and savings goals. Suggestions are never applied without consent. */
export function GoalsScreen(): JSX.Element {
  const { t, locale, user, categoryLabel, categoryMeta, categories, toast, toastError } = useApp();
  const money = useMoney();
  const privateMoney = usePrivateMoney();
  const currency = user?.currency ?? 'AED';

  const budgets = useFetch<{ budgets: BudgetProgress[] }>('/budgets');
  const goals = useFetch<{ goals: Goal[]; awaitingSalaryMinor?: number }>('/goals');
  const suggestions = useFetch<{
    suggestions: Suggestion[]; note: string; dismissedCount: number;
  }>('/budgets/suggestions');

  // What the budget sheet is currently doing. Null is closed; the shape says
  // whether it is creating from nothing, adjusting a suggestion before it
  // becomes a limit, or changing a limit that already exists.
  const [budgetSheet, setBudgetSheet] = useState<BudgetSheetMode | null>(null);
  // null = closed, 'new' = create, a Goal = edit that one.
  const [goalSheet, setGoalSheet] = useState<null | 'new' | Goal>(null);

  const removeGoal = async (goal: Goal) => {
    if (!window.confirm(`${t('deleteGoalConfirm')}\n\n${goal.name}`)) return;
    try {
      await api.del(`/goals/${goal.id}`);
      goals.reload();
      toast(t('done'));
    } catch (err) {
      toastError(err);
    }
  };

  const acceptSuggestion = async (s: Suggestion) => {
    try {
      await api.post('/budgets', {
        category: s.category, amountMinor: s.suggestedAmountMinor, period: 'monthly', fromSuggestion: true,
      });
      toast(t('done'));
      budgets.reload();
      suggestions.reload();
    } catch (err) {
      toastError(err);
    }
  };

  const declineSuggestion = async (s: Suggestion) => {
    try {
      await api.post(`/budgets/suggestions/${encodeURIComponent(s.category)}/dismiss`, {});
      suggestions.reload();
    } catch (err) {
      toastError(err);
    }
  };

  /**
   * Clears the whole list in one tap. Declining ten categories individually is
   * ten taps to say one thing, and the list is rebuilt every month.
   */
  const declineAllSuggestions = async () => {
    try {
      const { dismissed } = await api.post<{ dismissed: number }>('/budgets/suggestions/dismiss-all', {});
      // Named rather than a bare "Done", because the undo below is the only
      // thing standing between this button and a list the user cannot recover.
      toast(locale === 'ar'
        ? `${dismissed} اقتراح مخفي`
        : `${dismissed} suggestion${dismissed === 1 ? '' : 's'} hidden`);
      suggestions.reload();
    } catch (err) {
      toastError(err);
    }
  };

  const restoreSuggestions = async () => {
    try {
      await api.del('/budgets/suggestions/dismissed');
      suggestions.reload();
    } catch (err) {
      toastError(err);
    }
  };

  const removeBudget = async (id: string) => {
    try {
      await api.del(`/budgets/${id}`);
      budgets.reload();
      suggestions.reload();
    } catch (err) {
      toastError(err);
    }
  };

  return (
    <div className="screen">
      <h1 style={{ fontSize: 21, fontWeight: 680, marginBottom: 6 }}>{t('goals')}</h1>

      <SectionTitle action={
        <button className="btn btn--ghost btn--sm" onClick={() => setBudgetSheet({ kind: 'create' })}>+ {t('createBudget')}</button>
      }>
        {t('budgets')}
      </SectionTitle>

      {budgets.loading && <Skeleton count={2} />}
      {budgets.data && (budgets.data.budgets.length === 0 ? (
        <Card><div className="small muted center" style={{ padding: 10 }}>{t('none')}</div></Card>
      ) : (
        <Card>
          {budgets.data.budgets.map((b) => (
            <div key={b.id} style={{ paddingBlock: 11 }}>
              <div className="row row--between" style={{ marginBottom: 7 }}>
                <span className="row" style={{ gap: 8 }}>
                  <span aria-hidden>{categoryMeta(b.category).icon}</span>
                  <span className="small strong">{categoryLabel(b.category)}</span>
                </span>
                <span className="small money">
                  {money(b.spentMinor, currency, { compact: true })}
                  <span className="muted"> {t('budgetOf')} {money(b.budgetMinor, currency, { compact: true })}</span>
                </span>
              </div>
              <div className="bar">
                <span className="bar__fill" style={{
                  width: `${Math.min(100, b.progressPct)}%`,
                  background: b.status === 'exceeded' ? 'var(--negative)'
                    : b.status === 'warning' ? 'var(--warning)' : 'var(--accent)',
                }} />
              </div>
              {/* The window was never named, so a bar that reset at midnight
                  read as a daily limit. It says which window it is, and how
                  much of it is left, because a bar alone gives no sense of
                  whether you are ahead or behind. */}
              <div className="row row--between" style={{ marginTop: 5, gap: 10 }}>
                <span className="tiny muted">
                  {b.progressPct}% {t('of')} {PERIOD_WORD[b.period]?.[locale === 'ar' ? 'ar' : 'en'] ?? b.period}
                  {b.daysRemaining > 0 && ` · ${b.daysRemaining} ${
                    locale === 'ar' ? 'يوم متبقٍ' : b.daysRemaining === 1 ? 'day left' : 'days left'}`}
                </span>
                {/* A limit set once is a guess about a month that had not
                    happened yet. Changing it needed deleting and recreating,
                    which loses the row and reads as a heavier act than it is. */}
                <span className="row" style={{ gap: 14, flexShrink: 0 }}>
                  <button className="tiny" style={{ color: 'var(--accent-text)', background: 'none', fontWeight: 620 }}
                          onClick={() => setBudgetSheet({
                            kind: 'edit', id: b.id, category: b.category, amountMinor: b.budgetMinor,
                          })}>
                    {t('edit')}
                  </button>
                  <button className="tiny" style={{ color: 'var(--text-tertiary)', background: 'none' }}
                          onClick={() => removeBudget(b.id)}>
                    {t('delete')}
                  </button>
                </span>
              </div>
            </div>
          ))}
        </Card>
      ))}

      {/* What the budget figures do and do not cover.
          Moved off Home, where it was two lines of prose under a card meant to
          be glanced at. This is the screen where budgets are managed, so it is
          the screen where the rule belongs. */}
      {budgets.data && budgets.data.budgets.length > 0 && (
        <p className="tiny muted" style={{ marginTop: 8, lineHeight: 1.6 }}>
          {locale === 'ar'
            ? 'تُحتسب الميزانيات على الفئات التي حدّدتها فقط، وتشمل كل حساباتك لا الحساب المعروض وحده.'
            : 'Budgets count only the categories you set a limit for, and cover every account rather than just the one on screen.'}
        </p>
      )}

      {suggestions.data && (suggestions.data.suggestions.length > 0 || (suggestions.data.dismissedCount ?? 0) > 0) && (
        <>
          <SectionTitle action={<ProvenanceBadge kind="estimated" />}>{t('suggestedBudgets')}</SectionTitle>
          {suggestions.data.suggestions.length > 0 && (
            <>
              <p className="tiny muted" style={{ marginBottom: 8 }}>{t('suggestedHelp')}</p>
              <Card>
                {/* Two lines per row rather than one. Four controls beside a
                    two-line label squeezed the category column until the
                    average wrapped mid-figure; the supporting line gets the
                    full width instead. */}
                {suggestions.data.suggestions.map((s) => (
                  <div key={s.category} style={{ paddingBlock: 10 }}>
                    <div className="row row--between" style={{ gap: 10 }}>
                      <span className="small strong grow truncate">{categoryLabel(s.category)}</span>
                      <span className="small strong money">{money(s.suggestedAmountMinor, currency, { compact: true })}</span>
                      <button className="btn btn--secondary btn--sm" onClick={() => acceptSuggestion(s)}>{t('accept')}</button>
                      <button
                        className="icon-btn icon-btn--danger"
                        onClick={() => declineSuggestion(s)}
                        aria-label={locale === 'ar' ? `رفض ${categoryLabel(s.category)}` : `Decline ${categoryLabel(s.category)}`}
                      >
                        ✕
                      </button>
                    </div>
                    <div className="tiny muted" style={{ marginTop: 2 }}>
                      {/* Why this category and not one of the others they
                          spend in. A ranked list with no stated basis reads as
                          the same list everyone gets. */}
                      {s.reason === 'rising'
                        ? (locale === 'ar' ? 'في ارتفاع' : 'rising')
                        : s.swingMinor
                          ? (locale === 'ar'
                              ? `يتفاوت بـ ${money(s.swingMinor, currency, { compact: true })}`
                              : `swings by ${money(s.swingMinor, currency, { compact: true })}`)
                          : (locale === 'ar' ? 'متوسطك' : 'your average')}
                      {' · '}
                      {locale === 'ar' ? 'متوسط' : 'avg'} {money(s.averageMinor, currency, { compact: true })}
                      {' · '}
                      {/* The figure is an average rounded up, so disagreeing
                          with it is the normal case rather than an edge one. */}
                      <button className="tiny" style={{ color: 'var(--accent-text)', background: 'none', padding: 0, fontWeight: 620 }}
                              onClick={() => setBudgetSheet({ kind: 'suggestion', category: s.category, amountMinor: s.suggestedAmountMinor })}>
                        {locale === 'ar' ? 'تعديل المبلغ' : 'Edit amount'}
                      </button>
                    </div>
                  </div>
                ))}
              </Card>

              {/* One tap to say "none of these", rather than one per row. Only
                  worth offering when there is more than one row to clear —
                  below that it is the same tap as the ✕ beside it. */}
              {suggestions.data.suggestions.length > 1 && (
                <button className="btn btn--secondary btn--block btn--sm"
                        style={{ marginTop: 10 }}
                        onClick={declineAllSuggestions}>
                  {locale === 'ar' ? 'تجاهل الكل' : 'Dismiss all'}
                </button>
              )}

            </>
          )}
          {(suggestions.data.dismissedCount ?? 0) > 0 && (
            <p className="tiny muted" style={{ marginTop: 8, lineHeight: 1.6 }}>
              {locale === 'ar'
                ? `${suggestions.data.dismissedCount} اقتراح مخفي. `
                : `${suggestions.data.dismissedCount} suggestion${suggestions.data.dismissedCount === 1 ? '' : 's'} hidden. `}
              <button className="tiny" style={{ color: 'var(--accent-text)', background: 'none', padding: 0, fontWeight: 620 }}
                      onClick={restoreSuggestions}>
                {locale === 'ar' ? 'إظهارها' : 'Show them again'}
              </button>
            </p>
          )}
        </>
      )}

      <SectionTitle action={
        <button className="btn btn--ghost btn--sm" onClick={() => setGoalSheet('new')}>+ {t('createGoal')}</button>
      }>
        {t('savingsGoals')}
      </SectionTitle>

      {goals.loading && <Skeleton count={1} height={120} />}
      {goals.data && (goals.data.goals.length === 0 ? (
        <Empty icon="🎯" title={t('none')} />
      ) : (
        <div className="stack">
          {goals.data.goals.map((g) => (
            <Card key={g.id}>
              <div className="row row--between" style={{ marginBottom: 10 }}>
                <span className="strong">{g.name}</span>
                {g.onTrack != null && (
                  <span className={`badge badge--${g.onTrack ? 'confirmed' : 'warning'}`}>
                    {g.onTrack ? t('onTrack') : t('offTrack')}
                  </span>
                )}
              </div>

              <div className="row row--between" style={{ marginBottom: 7 }}>
                <span className="small money strong">{privateMoney(g.currentAmountMinor, currency)}</span>
                <span className="small muted money">{privateMoney(g.targetAmountMinor, currency)}</span>
              </div>
              <div className="bar" style={{ height: 8 }}>
                <span className="bar__fill" style={{ width: `${Math.min(100, g.progressPct)}%`, background: 'var(--accent)' }} />
              </div>

              {/* Where part of this goal's balance came from. Under the goal
                  it went into, not floating above the list: the money lands in
                  one specific goal, and said in the abstract it explained
                  nothing about the number directly above it. */}
              {(g.thisCycleMinor ?? 0) > 0 && (
                <div className="tiny muted" style={{ marginTop: 7, lineHeight: 1.5 }}>
                  {locale === 'ar'
                    ? `منها ${privateMoney(g.thisCycleMinor ?? 0, currency)} من ادخار هذه الدورة`
                    : `Includes ${privateMoney(g.thisCycleMinor ?? 0, currency)} from this cycle's saving`}
                </div>
              )}

              {/* Only before the salary lands. Afterwards it has been credited
                  and appears in the line above, so saying it here too would
                  read as a second, separate amount. */}
              {(g.thisCycleMinor ?? 0) === 0 && (goals.data?.awaitingSalaryMinor ?? 0) > 0 && (
                <div className="tiny muted" style={{ marginTop: 7, lineHeight: 1.5 }}>
                  {locale === 'ar'
                    ? `يُضاف ${privateMoney(goals.data?.awaitingSalaryMinor ?? 0, currency)} عند وصول الراتب القادم`
                    : `Your next salary adds ${privateMoney(goals.data?.awaitingSalaryMinor ?? 0, currency)}`}
                </div>
              )}

              <div className="stack stack--tight" style={{ marginTop: 14 }}>
                {g.targetDate && (
                  <Row label={t('targetDate')} value={formatDate(g.targetDate, locale)} />
                )}
                {g.requiredMonthlyMinor != null && (
                  <Row label={t('requiredMonthly')}
                       value={privateMoney(g.requiredMonthlyMinor, currency)}
                       badge="estimated" />
                )}
                {g.estimatedMonthlySavingMinor != null && (
                  <Row label={locale === 'ar' ? 'ما تدّخره فعلياً' : 'What you actually save'}
                       value={privateMoney(g.estimatedMonthlySavingMinor, currency)}
                       badge="estimated" />
                )}
              </div>

              {/* Saved-so-far in particular needs editing: nothing updates it
                  automatically, so without this a goal's progress can only ever
                  be whatever it was on the day it was created. */}
              <div className="row row--between" style={{ marginTop: 14 }}>
                <button className="btn btn--secondary btn--sm" onClick={() => setGoalSheet(g)}>
                  {t('edit')}
                </button>
                <button className="tiny" style={{ color: 'var(--text-tertiary)', background: 'none' }}
                        onClick={() => removeGoal(g)}>
                  {t('delete')}
                </button>
              </div>
            </Card>
          ))}
        </div>
      ))}

      {budgetSheet && (
        <BudgetSheet
          mode={budgetSheet}
          currency={currency}
          categories={categories.map((c) => ({ key: c.key, label: locale === 'ar' ? c.ar : c.en }))}
          onClose={() => setBudgetSheet(null)}
          onSaved={() => { budgets.reload(); suggestions.reload(); }}
        />
      )}

      {goalSheet && (
        <GoalSheet
          currency={currency}
          goal={goalSheet === 'new' ? undefined : goalSheet}
          onClose={() => setGoalSheet(null)}
          onSaved={() => goals.reload()}
        />
      )}
    </div>
  );
}

function Row({ label, value, badge }: { label: string; value: string; badge?: 'estimated' }): JSX.Element {
  return (
    <div className="row row--between">
      <span className="small muted row" style={{ gap: 6 }}>
        {label}
        {badge && <ProvenanceBadge kind={badge} />}
      </span>
      <span className="small strong money">{value}</span>
    </div>
  );
}

function BudgetSheet({ mode, currency, categories, onClose, onSaved }: {
  mode: BudgetSheetMode;
  currency: string;
  categories: { key: string; label: string }[];
  onClose: () => void;
  onSaved: () => void;
}): JSX.Element {
  const { t, locale, categoryLabel, toast } = useApp();
  const editing = mode.kind === 'edit';
  const [category, setCategory] = useState(
    mode.kind === 'create' ? categories[0]?.key ?? 'groceries' : mode.category,
  );
  const [amount, setAmount] = useState(
    mode.kind === 'create' ? '' : minorToDecimal(mode.amountMinor, currency).toString(),
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const save = async () => {
    const minor = parseAmountInput(amount, currency);
    if (!minor) { setError('—'); return; }
    setBusy(true);
    try {
      if (mode.kind === 'edit') {
        // Patch the existing row rather than posting a fresh budget. Posting
        // would work — the insert upserts on category — but it would report a
        // creation for something that already existed, and the audit log is
        // worth keeping honest.
        await api.patch(`/budgets/${mode.id}`, { amountMinor: minor });
      } else {
        await api.post('/budgets', {
          category, amountMinor: minor, period: 'monthly',
          // Still from a suggestion even when the figure was changed. Editing
          // the number does not make it the user's own idea.
          ...(mode.kind === 'suggestion' ? { fromSuggestion: true } : {}),
        });
      }
      toast(t('done'));
      onSaved();
      onClose();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Sheet
      title={editing
        ? (locale === 'ar' ? 'تعديل الميزانية' : 'Edit budget')
        : t('createBudget')}
      onClose={onClose}
    >
      <div className="stack">
        {/* Fixed when editing. Moving an existing limit to another category is
            not an edit of it — it is this budget deleted and a different one
            made — and quietly rewriting the category would carry the spending
            history of one category onto another. */}
        {editing ? (
          <Field label={t('category')}>
            <div className="input" style={{ display: 'flex', alignItems: 'center', color: 'var(--text-secondary)' }}>
              {categoryLabel(category)}
            </div>
          </Field>
        ) : (
          <Field label={t('category')}>
            <select className="select" value={category} onChange={(e) => setCategory(e.target.value)}>
              {categories.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
            </select>
          </Field>
        )}
        <Field
          label={`${t('amount')} (${currency}) · ${t('perMonth')}`}
          hint={locale === 'ar'
            ? 'حدّ لكامل الشهر التقويمي، يتراكم من اليوم الأول ويبدأ من جديد مع بداية الشهر التالي. لا يشمل بطاقة بعينها — يغطي كل حساباتك.'
            : 'A limit for the whole calendar month. It adds up from the 1st and starts again on the 1st of next month. It covers every account, not one card.'}
          error={error}
        >
          <input className="input" inputMode="decimal" value={amount}
                 onChange={(e) => setAmount(e.target.value)} placeholder="0.00" />
        </Field>
        <button className="btn btn--primary btn--block" disabled={busy} onClick={save}>{t('save')}</button>
      </div>
    </Sheet>
  );
}

/**
 * Create or edit a savings goal.
 *
 * One sheet for both, because the fields are identical — a separate edit form
 * would be the same inputs with a different verb, and the two would drift.
 */
function GoalSheet({ currency, goal, onClose, onSaved }: {
  currency: string;
  /** Undefined when creating. */
  goal?: Goal;
  onClose: () => void;
  onSaved: () => void;
}): JSX.Element {
  const { t, locale, toast } = useApp();
  const [name, setName] = useState(goal?.name ?? '');
  const [target, setTarget] = useState(
    goal ? String(minorToDecimal(goal.targetAmountMinor, currency)) : '',
  );
  const [current, setCurrent] = useState(
    goal ? String(minorToDecimal(goal.currentAmountMinor, currency)) : '',
  );
  const [date, setDate] = useState(goal?.targetDate ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const save = async () => {
    const targetMinor = parseAmountInput(target, currency);
    if (!name.trim()) {
      setError(locale === 'ar' ? 'الاسم مطلوب' : 'A name is required');
      return;
    }
    if (!targetMinor) {
      setError(locale === 'ar' ? 'حدّد مبلغاً مستهدفاً' : 'Set a target amount');
      return;
    }

    setBusy(true);
    setError(null);
    try {
      const payload = {
        name: name.trim(),
        targetAmountMinor: targetMinor,
        currentAmountMinor: parseAmountInput(current, currency) ?? 0,
        targetDate: date || null,
      };
      if (goal) await api.patch(`/goals/${goal.id}`, payload);
      else await api.post('/goals', payload);
      toast(t('done'));
      onSaved();
      onClose();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Sheet title={goal ? t('editGoal') : t('createGoal')} onClose={onClose}>
      <div className="stack">
        <Field label={t('goalName')}>
          <input className="input" value={name} onChange={(e) => setName(e.target.value)} autoFocus />
        </Field>
        <Field label={`${t('target')} (${currency})`}>
          <input className="input" inputMode="decimal" value={target} onChange={(e) => setTarget(e.target.value)} />
        </Field>
        <Field label={`${t('saved')} (${currency})`}>
          <input className="input" inputMode="decimal" value={current} onChange={(e) => setCurrent(e.target.value)} placeholder="0" />
        </Field>
        <Field label={t('targetDate')} error={error}>
          <input className="input" type="date" value={date} onChange={(e) => setDate(e.target.value)} />
        </Field>
        <button className="btn btn--primary btn--block" disabled={busy} onClick={save}>{t('save')}</button>
      </div>
    </Sheet>
  );
}
